import type { ContentClass, TitleFormat } from '@Types/Shared/Common';
import type { BlockKey } from '@Types/Shared/Options';

import type { ColumnFrontType, ColumnType, perRowsType } from '@/src/Types/Blocks/Columns';
import { type HeadingType } from '@/src/Types/Shared/Heading';
import type { ColumnLink, ColumnUrl } from '@/src/Types/Shared/RepeaterColumn';
import Heading from '@/src/Views/Shared/Heading/Heading';
import { Link } from '@inertiajs/react';
import Html from '@Views/Shared/Html';

import { SpanButton } from '../components/SpanButton';

export default function ColumnClassicTitle(props: ColumnFrontType) {
	const {
		id,
		titleFormat,
		title,
		perRow,
		columns,
		description,
		containerType,
		cardType,
		headingAlign,
		headingFontClassName,
		contentFontClass,
		context,
		sectionDecoration,
	} = props;

	const headingProps: HeadingType = {
		title: title,
		description: description,
		titleFormat: titleFormat,
		headingAlign: headingAlign,
		headingFontClassName: headingFontClassName,
		context: context,
	};

	let blockStyle = '';
	if (containerType === 'block') {
		blockStyle =
			'desktop:p-12 tablet:p-5 p-4 bg-minos-column-bg-container-color border-minos-column-container-border-width border-minos-column-container-border-color rounded-minos-column-container';
	} else {
		blockStyle = '';
	}

	let cardStyle = '';
	if (cardType === 'block') {
		cardStyle =
			'desktop:p-6 tablet:p-5 p-4 bg-minos-column-bg-card-color border-minos-column-card-border-width rounded-minos-column-card border-minos-column-card-border-color';
	} else {
		cardStyle = '';
	}

	let padding = '';
	if (sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'wave') {
		padding = 'pt-8 pb-16 tablet:pt-12 tablet:pb-24 desktop:pb-32';
	} else {
		padding = 'py-3.5 tablet:py-6 desktop:py-12';
	}

	return (
		<div id={id} className={`minos-grid relative bg-minos-column-bg-color ${padding}`}>
			<div data-aos='fade-up' className={`relative z-[11] col-start-2 col-end-14 ${blockStyle}`}>
				{/* mobile breakpoints handle in component */}
				<Heading {...headingProps} />

				<div className='grid grid-cols-4 gap-y-5 tablet:grid-cols-12 tablet:gap-8'>
					{columns?.map((column: ColumnType) => {
						return (
							<Column
								key={column.id}
								column={column}
								perRow={perRow}
								cardType={cardStyle}
								contentFontClass={contentFontClass}
								context={context}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
}

const Column = ({
	column,
	perRow,
	cardType,
	contentFontClass,
	context,
}: {
	column: ColumnType;
	perRow: perRowsType;
	cardType: string;
	contentFontClass?: ContentClass;
	context?: BlockKey;
}) => {
	const { id, title, description, link, imageObj, titleFormat = 'h2' } = column;

	const props = {
		id: id,
		title: title,
		description: description,
		image: imageObj ? imageObj.url : '',
		imageAlt: imageObj ? imageObj.alt : '',
		link: link,
		titleFormat: titleFormat,
		cardType: cardType,
		context: context,
		contentFontClass: contentFontClass,
	};

	const views = {
		1: <ColumnPerRow className='col-span-full flex flex-col gap-3' {...props} />,
		2: <ColumnPerRow className='col-span-full flex flex-col gap-3 tablet:col-span-6' {...props} />,
		3: <ColumnPerRow className='col-span-full flex flex-col gap-3 tablet:col-span-4' {...props} />,
		4: <ColumnPerRow className='col-span-full flex flex-col gap-3 tablet:col-span-3' {...props} />,
		6: <ColumnPerRow className='col-span-full flex flex-col gap-3 tablet:col-span-2' {...props} />,
	};

	return <>{views[perRow]}</>;
};

type ColumnPerRowType = {
	id: string;
	imageAlt: string | undefined;
	image: string | undefined;
	title: string;
	description: string;
	link?: ColumnLink;
	className: string | '';
	cardType: string;
	contentFontClass?: ContentClass;
	context?: BlockKey;
	titleFormat: TitleFormat;
};

const ColumnPerRow = (props: ColumnPerRowType) => {
	const {
		id,
		image,
		imageAlt,
		title,
		description,
		link,
		className,
		cardType,
		contentFontClass,
		context,
		titleFormat,
	} = props;

	// Animation btn
	let animBtn;

	const animBtnPrimary = contentFontClass?.text?.btnPrimary?.animation;
	const animBtnSecondary = contentFontClass?.text?.btnSecondary?.animation;
	const animBtnTertiary = contentFontClass?.text?.btnTertiary?.animation;

	if (link?.style === 'primary') {
		animBtn = animBtnPrimary;
	} else if (link?.style === 'secondary') {
		animBtn = animBtnSecondary;
	} else if (link?.style === 'tertiary') {
		animBtn = animBtnTertiary;
	}

	const linkUrl: ColumnUrl | undefined = link?.url as ColumnUrl | undefined;

	const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | any) => {
		if (linkUrl?.opensInNewTab) {
			event.preventDefault();
			window.open(linkUrl.url, '_blank');
		}
	};

	return (
		<>
			{linkUrl?.url ? (
				<Link
					href={linkUrl.url}
					onClick={handleLinkClick}
					data-aos='fade-up'
					id={id}
					className={`group ${className} ${cardType}`}
				>
					{image ? (
						<>
							<div className='relative'>
								{title && (
									<>
										{titleFormat === 'h2' ? (
											<h2
												className={`absolute inset-x-3 bottom-2.5 z-20 text-3xl tablet:inset-x-5 desktop:inset-x-6 ${contentFontClass?.title}`}
											>
												{title}
											</h2>
										) : (
											<h3
												className={`absolute inset-x-3 bottom-2.5 z-20 text-2xl tablet:inset-x-5 desktop:inset-x-6 ${contentFontClass?.title}`}
											>
												{title}
											</h3>
										)}
									</>
								)}

								<div
									className='absolute left-0 top-0 z-10 size-full rounded-minos-column-img bg-gradient-to-t from-minos-column-img-opacity-color to-transparent'
									aria-hidden='true'
								></div>
								<img
									className='aspect-[5/3] w-full rounded-minos-column-img object-cover'
									src={image}
									alt={imageAlt}
								/>
							</div>
						</>
					) : (
						<>{title && <h3 className={contentFontClass?.title}>{title}</h3>}</>
					)}

					{description && (
						<div data-content='minos-content' className=''>
							<Html context={context} className={contentFontClass?.text} html={description} />
						</div>
					)}
					<SpanButton
						label={link?.label ? link?.label : ''}
						style={link?.style ? link?.style : 'primary'}
						btnHasIcon={link?.btnHasIcon}
						btnDecorationElement={link?.btnDecorationElement}
						animation={animBtn}
						primaryClassname='minos-column-btn-primary tablet:minos-column-btn-primary transition-all group-hover:minos-column-btn-primary-hover group-hover:ease-in-out'
						secondaryClassname='minos-column-btn-secondary tablet:minos-column-btn-secondary group-hover:minos-column-btn-secondary-hover'
						tertiaryClassname='minos-column-btn-tertiary  group-hover:minos-column-btn-tertiary-hover'
						urlType={linkUrl.type}
					/>
				</Link>
			) : (
				<div data-aos='fade-up' id={id} className={`${className} ${cardType}`}>
					{image ? (
						<>
							<div className='relative'>
								{title && (
									<>
										{titleFormat === 'h2' ? (
											<h2
												className={`absolute inset-x-3 bottom-0 z-20 mb-2 text-3xl tablet:inset-x-5 tablet:mb-4 desktop:inset-x-6 ${contentFontClass?.title}`}
											>
												{title}
											</h2>
										) : (
											<h3
												className={`absolute inset-x-3 bottom-0 z-20 mb-2 text-2xl tablet:inset-x-5 tablet:mb-4 desktop:inset-x-6 ${contentFontClass?.title}`}
											>
												{title}
											</h3>
										)}
									</>
								)}

								<div
									className='absolute left-0 top-0 z-10 size-full rounded-minos-column-img bg-gradient-to-t from-minos-column-img-opacity-color to-transparent'
									aria-hidden='true'
								></div>
								<img
									className='aspect-[5/3] w-full rounded-minos-column-img object-cover'
									src={image}
									alt={imageAlt}
								/>
							</div>
						</>
					) : (
						<>{title && <h3 className={contentFontClass?.title}>{title}</h3>}</>
					)}

					{description && (
						<div data-content='minos-content' className='mb-3 last:mb-0 tablet:mb-3.5'>
							<Html context={context} className={contentFontClass?.text} html={description} />
						</div>
					)}
				</div>
			)}
		</>
	);
};
